/**
 * The vehicles of a route section (#90, 1.96.0) and their pictures.
 *
 * **One drawing for two places.** The same shapes stand as an SVG in the
 * sheets (`components/VehicleIcon.tsx`) and are painted onto the canvas of
 * the animation (`Path2D`). Two drawings of a car would drift apart, and the
 * vehicle in the film would not be the one chosen in the sheet.
 *
 * **Flat and in colour**, from use: "Die Symbole sollten farbig sein", with
 * examples of the style and the instruction not to copy them. Own drawings
 * on a 48 x 48 grid: filled shapes, no outline except where a light part
 * would vanish on the white disc of the film. **The colours belong to the
 * drawing and not to the appearance** -- like a photo, they stay the same in
 * light and dark mode, which is the one exception the rule "colours stand in
 * `tokens.css`" makes (what lies on a picture).
 *
 * **Side view, except the plane**, decided with the drawings: a car, a
 * camper or a walker is recognised from the side, a plane from above. A
 * side view faces **right** and is mirrored when the way goes west; the
 * plane faces right as well and is turned to its heading instead
 * (`view: 'top'`).
 *
 * **The camper is the author's own** (photos from use): a semi-integrated
 * motorhome on a Sprinter, black cab, light body. Its two sides are
 * decorated differently, and the film shows the one that faces the viewer:
 * going east the door side with the wave in the shape of Cape Cod, the
 * parasol and the shark,
 * going west the desert side with Joshua trees, rocks and a crescent moon
 * (from use: "wenn gespiegelt, auch die Folierung der anderen Seite
 * übernehmen").
 *
 * **The train is a steam locomotive**, from use: a modern train from the
 * side was not recognisable as one.
 *
 * **And since 1.136.0 there is a modern one beside it** (`express`), from
 * use: "zusätzlich noch ein modernes Zugmodell (z.B. ICE)". What makes it
 * one from the side is the long pointed nose; the white body, the dark
 * window band and the red stripe are what such trains share, not one
 * railway's livery copied. **Redrawn in 1.137.0**: the first drawing had
 * four car wheels under one short body, and from use it looked "eher wie
 * ein Wohnwagen". A train is long and runs on rails: two coupled cars,
 * small steel wheels in bogies, the rail beneath.
 */

/** The order the sheet offers them in, the same as `TrackSections::VEHICLES`. */
export const VEHICLES = ['car', 'camper', 'bus', 'bike', 'walk', 'train', 'express', 'ship', 'plane'] as const

export type Vehicle = (typeof VEHICLES)[number]

/** Without a choice on import: the car (from use, 5 Oct.). */
export const DEFAULT_VEHICLE: Vehicle = 'car'

export function isVehicle(value: unknown): value is Vehicle {
  return typeof value === 'string' && (VEHICLES as readonly string[]).includes(value)
}

/** One shape of a drawing: a path, its fill, and an outline where one is needed. */
export interface VehicleShape {
  d: string
  fill: string
  stroke?: string
  width?: number
}

export interface VehicleArt {
  /** `side`: mirrored when going west; `top`: turned to the heading. */
  view: 'side' | 'top'
  shapes: VehicleShape[]
  /**
   * The other side of the vehicle, for a side view going west: drawn facing
   * right like `shapes` and mirrored like them, so that the cab ends up on
   * the left. Only where the two sides differ (the camper's decoration).
   */
  otherSide?: VehicleShape[]
}

/** The shapes seen when travelling in a direction. */
export function shapesFor(art: VehicleArt, westward: boolean): VehicleShape[] {
  return westward && art.otherSide !== undefined ? art.otherSide : art.shapes
}

/** The grid the shapes are drawn on. */
export const VEHICLE_GRID = 48

/** A disc at (x, y) as a path: `Path2D` and SVG take the same string. */
const disc = (x: number, y: number, r: number) =>
  `M${x + r} ${y}a${r} ${r} 0 1 1-${2 * r} 0 ${r} ${r} 0 1 1 ${2 * r} 0z`

const TYRE = '#2b2f36'
const HUB = '#c9ced6'
const GLASS = '#a8d8ea'
const LIGHT = '#ffd166'
const OUTLINE = '#8a94a3'

/** The rail under a train, a line and nothing else; the 3D model leaves it out (`NOT_ON_MODEL`). */
export const RAIL = '#6f7782'
const STEEL = '#b4bbc4'
/** A wheel of a train: steel, small, no tyre. */
const railWheel = (x: number, y: number, r = 1.25): VehicleShape[] => [
  { d: disc(x, y, r), fill: STEEL },
]

/** A wheel: tyre and hub. */
const wheel = (x: number, y: number, r = 4.6, rim = HUB): VehicleShape[] => [
  { d: disc(x, y, r), fill: TYRE },
  { d: disc(x, y, r * 0.45), fill: rim },
]

/** A spiky leaf cluster: a star whose points alternate long and short. */
const tuft = (x: number, y: number, r: number, points = 9): string => {
  let d = ''
  for (let i = 0; i < points * 2; i++) {
    const angle = (Math.PI * i) / points - Math.PI / 2
    const length = i % 2 === 0 ? r : r * 0.4
    d += `${i === 0 ? 'M' : 'L'}${(x + length * Math.cos(angle)).toFixed(2)} ${(y + length * Math.sin(angle)).toFixed(2)}`
  }
  return d + 'z'
}

/**
 * A Joshua tree as on the wrap: a trunk, limbs that fork and bend upwards,
 * and a spiky cluster at every tip. `x`/`y` is the foot, `s` the scale.
 */
const joshuaTree = (x: number, y: number, s: number, colour: string): VehicleShape[] => {
  const at = (dx: number, dy: number) => `${(x + dx * s).toFixed(2)} ${(y + dy * s).toFixed(2)}`
  const tips: [number, number][] = [[-2.4, -9], [-3.6, -7.6], [-0.2, -9.8], [2, -9.4], [3.2, -7.8]]
  return [
    { d: `M${at(-0.5, 0)}L${at(-0.3, -6)}L${at(0.3, -6)}L${at(0.5, 0)}z`, fill: colour },
    {
      d: `M${at(0, -4.6)}L${at(-1.8, -6.6)}L${at(-2.4, -9)}M${at(-1.8, -6.6)}L${at(-3.6, -7.6)}` +
        `M${at(0, -5.8)}L${at(-0.2, -9.8)}M${at(0, -5.2)}L${at(1.8, -7)}L${at(2, -9.4)}M${at(1.8, -7)}L${at(3.2, -7.8)}`,
      fill: 'none', stroke: colour, width: 0.75 * s,
    },
    ...tips.map(([dx, dy]) => ({ d: tuft(x + dx * s, y + dy * s, 1.15 * s), fill: colour })),
  ]
}

/**
 * The wave on the door side, as bands: the same outline three times, each
 * copy moved a little down and towards the cab. What shows of the first is a
 * light band along the inside of the wave (the top of the low water at the
 * back, the face of the curl), the last fills the outside dark, as on the
 * wrap. Points on the back edge (x 3), at the cab (x 28) and on the bottom
 * (y 35) stay where they are, so no copy reaches past the body.
 */
const CAPE_COD: [string, ...number[]][] = [
  ['M', 3, 35], ['L', 3, 30.2],
  ['Q', 14, 30.4, 20.2, 28.6], ['Q', 24.2, 27.2, 24.8, 23.8], ['Q', 25.2, 21.4, 24.2, 20.6],
  ['Q', 23, 20.2, 21.7, 21.2], ['Q', 20.8, 16.8, 24.4, 15.2], ['Q', 27.8, 14.2, 28, 18.2],
  ['L', 28, 35],
]
const waveBand = (dx: number, dy: number): string => {
  const x = (v: number) => (v <= 3 || v >= 28 ? v : v + dx)
  const y = (v: number) => (v >= 35 ? v : v + dy)
  return CAPE_COD.map(([command, ...xy]) => {
    const out: string[] = []
    for (let i = 0; i < xy.length; i += 2) out.push(`${x(xy[i]!).toFixed(2)} ${y(xy[i + 1]!).toFixed(2)}`)
    return command + out.join(' ')
  }).join('') + 'z'
}

/** What both sides of the camper share: the body, the roof over the cab, the black cab, the wheels. */
const CAMPER_BODY: VehicleShape[] = [
  { d: 'M3 35V12q0-2 2-2h24v25z', fill: '#eceef1', stroke: OUTLINE, width: 0.8 },
  { d: 'M28 10h7q4.5 0 5.6 4.2l.6 2.8H28z', fill: '#dfe2e7', stroke: OUTLINE, width: 0.8 },
]
/**
 * The cab as a Sprinter's (from use, with a photo: "das Fahrerhaus sollte
 * noch mehr die Konturen des Sprinter bekommen"): the raked windscreen
 * running in under the overcab, a short bonnet falling to a rounded nose,
 * the headlight swept back along the wing, a flared arch. The 3D model
 * extrudes the same outline (`vehicle3d.ts`), so it is named once here.
 */
export const CAMPER_CAB_OUTLINE = 'M28 17H37.6L42.6 24.1Q43 24.5 43.6 24.6L45.6 25.1Q47 25.5 47.2 27L47.4 31.8Q47.5 34.4 46 35H28Z'
/** The rims are dark, as on the author's camper since (from use). */
export const CAMPER_RIM = '#4b525b'

/** Every fill a wheel is drawn in, a car's, a bike's, the camper's, a train's. */
const WHEEL_FILLS: ReadonlySet<string> = new Set([TYRE, HUB, '#d62828', CAMPER_RIM, STEEL])
/**
 * What the 3D model does not take from the drawing (`vehicle3d.ts`): the
 * wheels, which it builds itself and turns, and the rail, which would lie
 * under the ground. Named here, beside the drawings, so that a new wheel
 * colour cannot be forgotten in the other file.
 */
export const notOnModel = (s: VehicleShape): boolean =>
  (/^M[\d.]+ [\d.]+a/.test(s.d) && WHEEL_FILLS.has(s.fill)) || s.stroke === RAIL
const CAMPER_CAB: VehicleShape[] = [
  { d: CAMPER_CAB_OUTLINE, fill: '#1f2329' },
  { d: 'M28 33.2H45.2Q46.9 33.2 47.45 32.3Q47.4 34.5 46 35H28z', fill: '#121417' },
  { d: 'M34.2 18.2H37.3L41.5 24.3H34.2z', fill: GLASS },
  { d: 'M38.3 18.2L39.2 24.3', fill: 'none', stroke: '#1f2329', width: 0.6 },
  { d: 'M40.2 20.6q0-.6.6-.6h1q.6 0 .6.6v3.6q0 .6-.6.6h-1q-.6 0-.6-.6z', fill: '#15181c' },
  { d: 'M34.4 26.3h2.2v.6h-2.2z', fill: '#3a3f47' },
  { d: 'M42.4 25.9Q45.2 25.4 47.1 27.2L47.25 28.6Q45.6 28.4 44.4 27.9Q43.2 27.2 42.4 25.9z', fill: '#dfe6ec' },
  { d: 'M31.4 35A5.6 5.6 0 0 1 42.6 35z', fill: '#121417' },
  ...wheel(11, 35, 4.6, CAMPER_RIM),
  ...wheel(37, 35, 4.6, CAMPER_RIM),
]

export const VEHICLE_ART: Record<Vehicle, VehicleArt> = {
  car: {
    view: 'side',
    // The long bonnet and the raked windscreen are at the front (right), the
    // short boot and the steep rear window at the back. Drawn the other way
    // round until 1.97.1, and from use: "sieht aus, wie wenn es verkehrt
    // fährt" -- the shape said west while the headlight said east.
    shapes: [
      { d: 'M45 35V29Q45 26 42 25.5L35 24.5 28.5 18Q27 16.5 25 16.5H16Q14 16.5 12.8 18L8.5 24 5.5 24.8Q3 25.5 3 28V35Z', fill: '#2a9d8f' },
      { d: 'M26.5 19.6Q25.7 18.7 24.5 18.7H20.5V24.4H31.6Z', fill: GLASS },
      { d: 'M18.5 18.7H16Q14.8 18.7 14.1 19.7L10.7 24.4H18.5Z', fill: GLASS },
      { d: 'M3 30.5h42v1.6H3z', fill: '#21867a' },
      { d: 'M41.5 27h3.3v2.2h-3.3z', fill: LIGHT },
      { d: 'M3 27.4h2.2v2.4H3z', fill: '#9b2c24' },
      ...wheel(12, 35),
      ...wheel(35.5, 35),
    ],
  },
  camper: {
    view: 'side',
    // The door side: the beach parasol at the back, the door with the shark
    // coming out of the water, and the big wave by the cab. Its outline is
    // Cape Cod (from use: "das ist wichtig"): it rises by the cab like the
    // forearm and curls back over the top like the hook at Provincetown,
    // with a pale crest.
    shapes: [
      ...CAMPER_BODY,
      { d: 'M3 29.5q3.5-4.5 7.5-4t9 3.5V31H3z', fill: '#cfc8bc' },
      // The parasol as on the wrap: a flat cone, pale on top, a teal band
      // with a scalloped edge, a thin pole.
      { d: 'M9.6 21V31', fill: 'none', stroke: '#b9a37a', width: 0.5 },
      { d: 'M4.3 22.3H14.9L14.6 23.7q-0.625 .8 -1.250 0q-0.625 .8 -1.250 0q-0.625 .8 -1.250 0q-0.625 .8 -1.250 0q-0.625 .8 -1.250 0q-0.625 .8 -1.250 0q-0.625 .8 -1.250 0q-0.625 .8 -1.250 0z', fill: '#3d6b7d' },
      { d: 'M4.3 22.4Q9.6 19 14.9 22.4z', fill: '#efe2c0' },
      { d: 'M6 13.5h7v4.5H6z', fill: TYRE },
      { d: 'M15.5 14h5v13.5h-5z', fill: '#dfe2e7', stroke: OUTLINE, width: 0.7 },
      { d: 'M17.1 15.5h1.8v5h-1.8z', fill: TYRE },
      { d: waveBand(0, 0), fill: '#9cc5d8' },
      { d: waveBand(0.8, 1.1), fill: '#5b88a3' },
      { d: waveBand(1.7, 2.3), fill: '#3f6278' },
      { d: 'M28 18.6Q27.6 14 24.3 14.4 20.6 15 20.8 19.6 21.1 21.9 22.7 21.5 21.7 20.7 21.8 19.2 22 16.1 24.5 15.6 27.2 15.4 28 18.6z', fill: '#efe2c0' },
      // The shark as on the wrap: blue-grey head, pale face, a red mouth
      // full of teeth.
      { d: 'M16 27.4q.5-3.6 2-6.1 1.5 2.5 2 6.1z', fill: '#6e93ad' },
      { d: 'M16.2 27.4q.2-2.1 1.8-2.8 1.6.7 1.8 2.8z', fill: '#efe2c0' },
      { d: 'M16.7 27.2q.1-1.8 1.3-2.2 1.2.4 1.3 2.2z', fill: '#8b2e2a' },
      { d: 'M16.70 27.20L17.23 26.82L16.91 26.01L17.47 26.17L17.46 25.20L17.89 25.85L18.19 25.02L18.33 25.96L18.85 25.54L18.68 26.46L19.25 26.58L18.81 27.20z', fill: '#ffffff' },
      { d: 'M17.25 27.2q.1-1.1.75-1.35.65.25.75 1.35z', fill: '#8b2e2a' },
      { d: disc(16.8, 24.4, 0.18) + disc(19.2, 24.4, 0.18), fill: TYRE },
      // The splash at the shark's chin.
      { d: 'M15.4 27.6Q16.6 26.9 18 27.1T20.6 27.4L20.4 28.2H15.6z', fill: '#cfe4ee' },
      ...CAMPER_CAB,
      // The wave runs on over the black cab in a wide bulge, the elbow of
      // Cape Cod; the front wheel goes back on top of it.
      { d: 'M28 35V19.6Q32.2 20.6 33.6 26.4 34.4 31.2 33 35z', fill: '#4f7391' },
      { d: 'M28 35V19.6Q29.6 20 30.4 21.4 29.2 27 29.6 35z', fill: '#3f6278' },
      ...wheel(37, 35, 4.6, CAMPER_RIM),
    ],
    // The driver side, drawn facing right and mirrored: two windows near the
    // cab, a large one at the back, the locker below, and the desert.
    otherSide: [
      ...CAMPER_BODY,
      { d: 'M3 35v-4.5q7-2 13-1t13-1V35z', fill: '#e8dccb' },
      { d: 'M15.2 10.9a2 2 0 1 0 2.5 2.7 1.6 1.6 0 1 1-2.5-2.7z', fill: '#e9c46a' },
      ...joshuaTree(14.5, 31, 0.95, '#d9c3ad'),
      ...joshuaTree(22, 31.5, 1.15, '#b5673f'),
      { d: 'M15 34.5l2.4-3.6 2.8.8 1.8 2.8zM4 34.5l1.8-2.6 2.6.8 1 1.8zM22.5 34.5l1.6-2.2 2.2.6.9 1.6z', fill: '#7d8597' },
      { d: 'M17.5 14h4.5v4h-4.5zM23.5 14h4v4h-4zM4.5 13.5h9v4.5h-9z', fill: TYRE },
      // The locker as on the wrap: a pale saguaro, sand, boulders with a
      // dark face.
      { d: 'M4.5 22.5h5.6q.7 0 .7.7v6.1q0 .7-.7.7H4.5q-.7 0-.7-.7v-6.1q0-.7.7-.7z', fill: '#e4e6ea', stroke: OUTLINE, width: 0.6 },
      { d: 'M8.2 28.4V23.6M8.2 26.2H7.1V24.8M8.2 25.4h1.1V24.2', fill: 'none', stroke: '#d9c3ad', width: 0.7 },
      { d: 'M4.2 28.3q1.8-.9 3.4-.5t3.2-.3v1.9q0 .4-.4.4H4.6q-.4 0-.4-.4z', fill: '#e8dccb' },
      { d: 'M4.6 28.7q0-1.9 1.3-1.9t1.3 1.9zM8.3 28.8q0-1.5 1-1.5t1 1.5z', fill: '#c7ccd4' },
      { d: 'M6.1 28.7l.3-1.6q.8.4.8 1.6zM9.4 28.8l.2-1.3q.7.3.7 1.3zM5.9 29.6q.6-.6 1.2 0z', fill: '#3a3f47' },
      ...CAMPER_CAB,
    ],
  },
  bus: {
    view: 'side',
    shapes: [
      { d: 'M3 34V12q0-3 3-3h34q4 0 5 4l.5 21q0 1.5-1.5 1.5H4.5Q3 35.5 3 34z', fill: '#f4b740' },
      { d: 'M6 13h6v8H6zM14 13h6v8h-6zM22 13h6v8h-6z', fill: GLASS },
      { d: 'M38 13h4q1.6 0 2 2l.6 9H38z', fill: GLASS },
      { d: 'M30 13h6v21h-6z', fill: '#d9952a' },
      { d: 'M31 14h4v8h-4z', fill: GLASS },
      { d: 'M3 25h27v2H3z', fill: '#e07a5f' },
      { d: 'M43 29h2.5v2H43z', fill: LIGHT },
      ...wheel(11, 35.5),
      ...wheel(40, 35.5, 4.2),
    ],
  },
  bike: {
    view: 'side',
    shapes: [
      { d: disc(11, 32, 8), fill: 'none', stroke: TYRE, width: 2.6 },
      { d: disc(37, 32, 8), fill: 'none', stroke: TYRE, width: 2.6 },
      { d: 'M11 32H24L19.5 17zM19.5 17l13.5-1L24 32M33 16l4 16', fill: 'none', stroke: '#2a9d8f', width: 2.6 },
      { d: 'M16 15h7', fill: 'none', stroke: TYRE, width: 2.6 },
      { d: 'M30.5 11.5 33 16M29.5 11.5h4.5', fill: 'none', stroke: TYRE, width: 2.4 },
      { d: disc(24, 32, 1.8), fill: TYRE },
    ],
  },
  walk: {
    view: 'side',
    shapes: [
      // Legs, one in front, one behind, and shoes.
      { d: 'M23 26 18.5 38M28 26l3.5 12', fill: 'none', stroke: '#33415c', width: 4 },
      { d: 'M15.5 38.5h5v-2.2h-3.5zM29.5 36.3h4.5l1.8 2.2h-6.3z', fill: TYRE },
      // A rucksack, the shirt, the arms.
      { d: 'M16 15q0-2 2-2h3v11h-3q-2 0-2-2z', fill: '#e9c46a' },
      { d: 'M21 15q0-3 3-3h3q3 0 3 3v12H21z', fill: '#3a86ff' },
      { d: 'M27.5 15.5 31 23.5', fill: 'none', stroke: '#3a86ff', width: 3.2 },
      { d: 'M31 23.5h0', fill: 'none', stroke: '#f2c094', width: 3.4 },
      // The head.
      { d: disc(25.5, 7.5, 4), fill: '#f2c094' },
      { d: 'M21.5 7q.5-4.5 4.5-4.5t4 3.5z', fill: '#6d4c41' },
    ],
  },
  train: {
    view: 'side',
    // A steam locomotive: cab at the back, boiler, chimney and dome, the
    // smoke, big red driving wheels, the cowcatcher at the front.
    shapes: [
      { d: disc(36, 5.5, 2.6) + disc(31.5, 3.8, 2.1) + disc(27.5, 4.6, 1.6), fill: '#e5e7eb', stroke: OUTLINE, width: 0.6 },
      { d: 'M3 13h12v20H3z', fill: '#2d3a45' },
      { d: 'M1.8 10.5h15v3h-15z', fill: '#1c252d' },
      { d: 'M6 16h6v6H6z', fill: GLASS },
      { d: 'M15 18h24q3 0 3 3v8q0 3-3 3H15z', fill: '#2d6a4f' },
      { d: 'M21 18h2v14h-2zM31 18h2v14h-2z', fill: '#e9c46a' },
      { d: 'M38 18h2.5q3.5 0 3.5 3.5v7q0 3.5-3.5 3.5H38z', fill: '#2d3a45' },
      { d: 'M34 9h5l-.8 9h-3.4z', fill: '#2d3a45' },
      { d: 'M33 7.5h7v2.4h-7z', fill: '#1c252d' },
      { d: 'M24.5 18q0-4 3-4t3 4z', fill: '#e9c46a' },
      { d: 'M3 32h41v3H3z', fill: '#1c252d' },
      { d: 'M42 33.5h2.5l3 5.5H40z', fill: '#d62828' },
      { d: 'M42.4 23.5h1.8v2.6h-1.8z', fill: LIGHT },
      ...wheel(9, 37, 3.4, '#d62828'),
      ...wheel(20.5, 36, 5, '#d62828'),
      ...wheel(31.5, 36, 5, '#d62828'),
      { d: 'M20.5 36h11', fill: 'none', stroke: HUB, width: 1.6 },
    ],
  },
  express: {
    view: 'side',
    // A high-speed train, redrawn in 1.137.0. From use: "das ICE-Modell
    // sieht eher aus wie ein Wohnwagen. ein Zug hat keine Autoreifen." What
    // says train is the length and what runs on rails: the lead car with
    // its long pointed nose, the next car coupled behind it, both low over
    // a grey skirt, small steel wheels in dark bogies, and the rail. Above
    // that the window band, the red stripe and the pantograph.
    shapes: [
      { d: 'M1 37.2H47', fill: 'none', stroke: RAIL, width: 0.8 },
      { d: 'M20.2 17.4h5v.7h-5z', fill: '#5c6370' },
      { d: 'M21 17.4l2.6-3.2-1.8-1.4M21.4 12.8h3', fill: 'none', stroke: '#5c6370', width: 0.5 },
      { d: 'M1 32V20q0-2 2-2h10.2v14z', fill: '#f4f5f7', stroke: OUTLINE, width: 0.7 },
      { d: 'M13.2 19h1v12.4h-1z', fill: '#3a3f47' },
      { d: 'M14.2 32V20q0-2 2-2H33q7.2.4 11.6 7 2 3 2 5.4 0 1.6-1.6 1.6z', fill: '#f4f5f7', stroke: OUTLINE, width: 0.7 },
      { d: 'M1 21.2h12.2v3H1zM14.2 21.2h21l3.2 3H14.2z', fill: '#2b3a4a' },
      { d: 'M36.6 19.5q4 .6 6.8 4.9h-3.8z', fill: '#25343f' },
      { d: 'M5 21.2v3M9 21.2v3M18.5 21.2v3M23 21.2v3M27.5 21.2v3M32 21.2v3', fill: 'none', stroke: '#f4f5f7', width: 0.5 },
      { d: 'M1 26.4h12.2v1H1zM14.2 26.4h31.1l.5 1H14.2z', fill: '#c8102e' },
      { d: 'M43.4 29.4h2.2v.9h-2.2z', fill: LIGHT },
      { d: 'M1.4 32h11.6v1.4H1.4zM14.6 32h30.2q-.6 1.4-2.2 1.4h-28z', fill: '#9aa3ad' },
      { d: 'M3 33.2h7v2.4H3zM17.5 33.2h7v2.4h-7zM35 33.2h7v2.4h-7z', fill: '#3a3f47' },
      ...railWheel(4.6, 35.6), ...railWheel(8.4, 35.6),
      ...railWheel(19.1, 35.6), ...railWheel(22.9, 35.6),
      ...railWheel(36.6, 35.6), ...railWheel(40.4, 35.6),
    ],
  },
  ship: {
    view: 'side',
    shapes: [
      // Funnel, superstructure with bridge, hull with its red waterline.
      { d: 'M15 12h6l1 9h-8z', fill: '#e63946' },
      { d: 'M15 12h6v2.5h-6z', fill: TYRE },
      { d: 'M9 21h29v9H9z', fill: '#f4f5f7', stroke: OUTLINE, width: 0.8 },
      { d: 'M29 16h8v5h-8z', fill: '#f4f5f7', stroke: OUTLINE, width: 0.8 },
      { d: 'M31 17.5h4.5v2H31zM12 24h3v2.5h-3zM17 24h3v2.5h-3zM22 24h3v2.5h-3zM27 24h3v2.5h-3zM32 24h3v2.5h-3z', fill: '#264653' },
      { d: 'M2 30h44l-4.5 8h-35z', fill: '#264653' },
      { d: 'M4.2 34h39.6l-1.1 2H5.3z', fill: '#e63946' },
    ],
  },
  plane: {
    view: 'top',
    shapes: [
      // Wings and tailplane, then the fuselage over them, nose to the right.
      { d: 'M21 21.5 13.5 4h4.5l13 17.5zM21 26.5 13.5 44h4.5l13-17.5z', fill: '#3a86ff' },
      { d: 'M7 21.5 4 14h3.2l5.3 7.5zM7 26.5 4 34h3.2l5.3-7.5z', fill: '#3a86ff' },
      { d: 'M4.5 24q0-2.6 3-2.6H38q6.5.3 8.5 2.6-2 2.3-8.5 2.6H7.5q-3 0-3-2.6z', fill: '#f4f5f7', stroke: OUTLINE, width: 0.9 },
      { d: 'M40.5 23h3.5q1.3.4 1.3 1t-1.3 1h-3.5z', fill: '#264653' },
    ],
  },
}
